import React from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useTheme } from '@material-ui/core/styles'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { TopicIntentCount, pivotToHeatmapSeries } from './analysisTypes'

interface Props {
    open: boolean
    onClose: () => void
    data: TopicIntentCount[]
    drillInto: (
        dimension: 'topic' | 'intent',
        value: string,
        label: string,
    ) => void
}

export const HeatmapDialog = ({ open, onClose, data, drillInto }: Props) => {
    const theme = useTheme()
    const heatmapData = pivotToHeatmapSeries(data)

    const topicLookup = new Map<string, string>()
    const intentLookup = new Map<string, string>()
    for (const item of data) {
        topicLookup.set(item.topic_label, String(item.topic_id))
        intentLookup.set(item.intent_label, item.intent)
    }

    const options: ApexOptions = {
        chart: {
            type: 'heatmap',
            toolbar: { show: true },
            events: {
                dataPointSelection: (
                    _event: unknown,
                    _chartContext: unknown,
                    config: {
                        seriesIndex: number
                        dataPointIndex: number
                    },
                ) => {
                    const series = heatmapData[config.seriesIndex]
                    const point = series?.data[config.dataPointIndex]
                    if (series && point) {
                        const topicValue = topicLookup.get(series.name)
                        const intentValue = intentLookup.get(point.x)
                        if (topicValue) {
                            drillInto('topic', topicValue, series.name)
                        }
                        if (intentValue) {
                            drillInto('intent', intentValue, point.x)
                        }
                        onClose()
                    }
                },
            },
        },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px' },
        },
        colors: [theme.palette.primary.main],
        xaxis: {
            labels: {
                show: true,
                rotate: -45,
                rotateAlways: true,
                style: { fontSize: '11px' },
            },
        },
        yaxis: {
            labels: {
                style: { fontSize: '11px' },
            },
        },
        tooltip: {
            y: {
                formatter: (val: number) => `${val} conversations`,
            },
        },
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="span">
                    Topic x Intent Heatmap
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    style={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {heatmapData.length > 0 ? (
                    <ReactApexChart
                        options={options}
                        series={heatmapData}
                        type="heatmap"
                        height={Math.max(400, heatmapData.length * 40)}
                    />
                ) : (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ padding: 24, textAlign: 'center' }}
                    >
                        No cross-tab data available
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    )
}
